import {
  action,
  card,
  section,
  type LocaleWithoutGerman,
  type LocalizedPageCopy,
} from './staticPageLocalizationModel.js';

export const globusCompanyPages: Record<LocaleWithoutGerman, LocalizedPageCopy> = {
  tr: {
    eyebrow: 'İş ortağı olarak GLOBUS',
    title: 'GLOBUS ve Viva Porta: mağazanın ihtiyaçlarına uyan personel kazanımı.',
    intro:
      'Bu iş birliği, somut bir personel ihtiyacının uygun profiller, açık hazırlık ve iş başlangıcına kadar destekle nasıl güvenilir bir işe alım sürecine dönüştüğünü gösteriyor.',
    heroGallery: [
      { imageAlt: 'İş ortaklığının simgesi olarak GLOBUS logosu' },
      { imageAlt: 'Viva Porta iş ortağı olarak GLOBUS mağazası' },
    ],
    cards: [
      card(
        'Mağazaya yakın ihtiyaç analizi',
        'Lokasyon, vardiya modeli, görevler ve ekip yapısı temelinde işletmenin gerçek koşullarını yansıtan net bir arama profili oluşturulur.',
      ),
      card(
        'Uygun profiller',
        'Viva Porta nitelik, pratik deneyim, dil seviyesi, motivasyon ve uygunluğu somut ihtiyaçla eşleştirir.',
      ),
      card(
        'Başlangıca kadar destek',
        'Seçim, hazırlık, ülkeye giriş ve yerleşme şirket ile çalışanlar için bağlantılı tek bir süreç olarak koordine edilir.',
      ),
    ],
    sections: [
      section(
        'GLOBUS örnek iş birliği',
        'Personel ihtiyacı güçlü bir ortaklığa dönüşüyor.',
        'GLOBUS, ihtiyaçların açık biçimde tanımlandığı ve başlangıçların birlikte hazırlandığı güçlü bir Viva Porta iş ortağıdır. Profilleri yalnızca iletmek yerine görevler, beklentiler ve koşullar erkenden netleştirilir. Böylece mağaza, ekip ve yeni çalışanlar için sağlam bir seçim ve planlanabilir bir başlangıç oluşur.',
        undefined,
        'Viva Porta ve GLOBUS temsilcileri ortaklık buluşmasında',
      ),
      section(
        'İşe alım alanları',
        'Taze ürün, zanaat ve gastronomi için nitelikli çalışanlar.',
        'Odak noktası fırın, kasap, taze ürün tezgâhı ve gastronomi profilleridir. Viva Porta mesleki deneyimin yanı sıra dil seviyesini, motivasyonu ve ekip uyumunu değerlendirir. Böylece genel bir personel ihtiyacı, somut roller ve lokasyonlar için iş ortağı eşleştirmesine dönüşür.',
        [
          card('Fırın', 'Üretim, ürün kalitesi, iş akışları ve müşteri odaklı satış için profiller.'),
          card('Kasap', 'Üretim, kesim, hijyen ve danışmanlık için mesleki açıdan uygun çalışanlar.'),
          card(
            'Taze ürün tezgâhı',
            'Ürün bilgisine, titizliğe ve danışmanlık isteğine sahip hizmet odaklı profiller.',
          ),
          card('Gastronomi', 'Hazırlık, servis, misafir iletişimi ve güvenilir ekip çalışması için çalışanlar.'),
        ],
        'İş ortaklığının simgesi olarak GLOBUS logosu',
      ),
    ],
    cta: {
      eyebrow: 'İş ortağımız olun',
      title: 'Nitelikli çalışanlar arıyor ve Viva Porta ile iş birliği yapmak mı istiyorsunuz?',
      text: 'Personel ihtiyacınızı, aranan nitelikleri ve uygun zaman planını birlikte netleştiririz. İş ortağı eşleştirmemiz sayesinde ön değerlendirmeden geçmiş uluslararası profillere ve iş başlangıcına kadar kişisel desteğe erişirsiniz.',
      imageAlt: 'Viva Porta ekibi bir GLOBUS mağazasının kasap bölümünü ziyaret ediyor',
      primaryAction: action('Uzman personel talep et'),
      secondaryAction: action('İş ortağı eşleştirmesini incele'),
    },
  },
  en: {
    eyebrow: 'GLOBUS as a partner',
    title: 'GLOBUS and Viva Porta: recruitment that fits the store.',
    intro:
      'This partnership shows how a specific staffing need becomes a reliable recruitment process with suitable profiles, clear preparation and guidance through to the first day at work.',
    heroGallery: [
      { imageAlt: 'GLOBUS logo as a symbol of the partnership' },
      { imageAlt: 'GLOBUS store as a partner of Viva Porta' },
    ],
    cards: [
      card(
        'Needs grounded in the store',
        'Location, shift model, responsibilities and team structure create a clear search profile that reflects operating reality.',
      ),
      card(
        'Suitable profiles',
        'Viva Porta matches qualifications, practical experience, language, motivation and availability with the specific need.',
      ),
      card(
        'Guidance through to the start',
        'Selection, preparation, entry and arrival are coordinated as one connected process for the company and its new employees.',
      ),
    ],
    sections: [
      section(
        'GLOBUS case study',
        'A staffing need becomes a dependable partnership.',
        'GLOBUS is a strong Viva Porta partner because requirements are stated openly and each start is prepared together. Instead of merely forwarding profiles, tasks, expectations and conditions are clarified early. This creates a sound selection and a predictable start for the store, the team and new employees.',
        undefined,
        'Representatives of Viva Porta and GLOBUS at their partnership meeting',
      ),
      section(
        'Recruitment fields',
        'Qualified employees for fresh food, skilled work and hospitality.',
        'The focus is on profiles for bakery, butchery, fresh-food counters and hospitality. Viva Porta reviews professional experience as well as language level, motivation and team fit. A general staffing need becomes partner matching for specific roles and locations.',
        [
          card('Bakery', 'Profiles for production, product quality, workflows and customer-facing sales.'),
          card('Butchery', 'Professionally suitable employees for production, cutting, hygiene and advice.'),
          card(
            'Fresh-food counter',
            'Service-focused profiles with product knowledge, care and an interest in customer advice.',
          ),
          card('Hospitality', 'Employees for preparation, service, guest contact and dependable teamwork.'),
        ],
        'GLOBUS logo as a symbol of the partnership',
      ),
    ],
    cta: {
      eyebrow: 'Become a partner',
      title: 'Are you looking for skilled employees and ready to work with Viva Porta?',
      text: 'We clarify your staffing needs, required qualifications and a realistic timeline. Our partner matching gives you access to pre-qualified international profiles and personal guidance through to the first day at work.',
      imageAlt: 'Viva Porta visiting the butchery department of a GLOBUS store',
      primaryAction: action('Request specialists'),
      secondaryAction: action('View partner matching'),
    },
  },
};
